<main class="grow vendor-accent" role="content">
  <div class="kt-container-fixed vendor-catalog-container py-5 lg:py-7.5">
    <div class="vendor-page-head mb-5">
      <div>
        <span class="vendor-eyebrow">Catalog</span>
        <h1 class="vendor-title">Vendor Directory</h1>
        <p class="vendor-subtitle">Vendor profiles, contact details, and billing concepts.</p>
      </div>
      <button type="button" class="kt-btn kt-btn-primary" (click)="openCreateVendor()">
        <i class="ki-filled ki-plus text-sm" aria-hidden="true"></i>
        <span>New vendor</span>
      </button>
    </div>

    <div class="kt-card vendor-grid-card">
      <div class="kt-card-header min-h-16 flex-wrap gap-3">
        <h2 class="kt-card-title">Vendors</h2>
        <input
          #vendorSearch
          type="text"
          class="kt-input sm:w-96"
          placeholder="Search by name, type, address, phone, currency, or concept..."
          [value]="searchTerm()"
          (input)="onSearchChange(vendorSearch.value)"
          aria-label="Search vendors"
        />
      </div>

      <div class="kt-card-content p-0">
        @if (loadError()) {
          <div class="kt-alert kt-alert-destructive m-5">{{ loadError() }}</div>
        }

        <div class="kt-card-table">
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table">
              <thead>
                <tr>
                  <th scope="col">Vendor Name</th>
                  <th scope="col">Vendor Type</th>
                  <th scope="col">Address</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Default Currency</th>
                  <th scope="col">Invoice Concepts</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                @for (vendor of pagedVendors(); track vendor.id) {
                  <tr>
                    <td>
                      <div class="font-medium">{{ vendor.name }}</div>
                      <div class="vendor-cell-muted">{{ vendor.description || 'No description' }}</div>
                    </td>
                    <td>
                      @if (vendor.vendorTypeName) {
                        <span class="vendor-type-tag">{{ vendor.vendorTypeName }}</span>
                      } @else {
                        -
                      }
                    </td>
                    <td class="vendor-cell-muted">{{ formatAddress(vendor) }}</td>
                    <td>{{ vendor.phoneNumber || '-' }}</td>
                    <td>{{ vendor.defaultCurrency || '-' }}</td>
                    <td class="vendor-cell-muted">{{ formatInvoiceTypes(vendor.emittedInvoiceTypes) || '-' }}</td>
                    <td class="text-end">
                      <div class="inline-flex items-center gap-2">
                        <a class="kt-btn kt-btn-sm kt-btn-outline" [routerLink]="['/catalogs/vendor', vendor.id]">
                          View
                        </a>
                        <button type="button" class="kt-btn kt-btn-sm kt-btn-destructive" (click)="deleteVendor(vendor)">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="7" class="text-center text-secondary-foreground py-8">No vendors found.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="flex items-center justify-between gap-3 flex-wrap px-5 py-4">
            <span class="text-sm text-secondary-foreground">{{ rangeLabel() }}</span>
            <div class="inline-flex items-center gap-2">
              <button
                type="button"
                class="kt-btn kt-btn-sm kt-btn-outline"
                [disabled]="pageNumber() <= 1"
                (click)="goToPreviousPage()"
              >
                Previous
              </button>
              <span class="text-sm text-secondary-foreground">Page {{ pageNumber() }} / {{ totalPages() }}</span>
              <button
                type="button"
                class="kt-btn kt-btn-sm kt-btn-outline"
                [disabled]="pageNumber() >= totalPages()"
                (click)="goToNextPage()"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  @if (isEditorOpen()) {
    <div class="vendor-modal-backdrop" (click)="closeEditor()" aria-hidden="true"></div>
    <section class="vendor-modal kt-card" role="dialog" aria-modal="true" aria-label="Create vendor">
      <div class="kt-card-header min-h-16">
        <h2 class="kt-card-title">Create vendor</h2>
      </div>
      <div class="kt-card-content p-0">
        @if (validationMessage()) {
          <div class="kt-alert kt-alert-destructive m-5">{{ validationMessage() }}</div>
        }

        <section class="vendor-section">
          <h3 class="vendor-section-title">Identification</h3>
          <div class="vendor-field-grid">
            <label class="field-block">
              <span class="field-label">Vendor Name *</span>
              <input
                #vendorName
                class="kt-input"
                [value]="form().name"
                (input)="onFieldChange('name', vendorName.value)"
                placeholder="Vendor legal name"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Vendor Type *</span>
              <select
                #vendorType
                class="kt-select"
                [value]="form().vendorTypeId ?? ''"
                (change)="onFieldChange('vendorTypeId', vendorType.value ? +vendorType.value : null)"
              >
                <option value="">Select a vendor type</option>
                @for (option of vendorTypeOptions(); track option.id) {
                  <option [value]="option.id" [selected]="form().vendorTypeId === option.id">{{ option.name }}</option>
                }
              </select>
            </label>

            <label class="field-block">
              <span class="field-label">Default Currency *</span>
              <input
                #defaultCurrency
                class="kt-input"
                [value]="form().defaultCurrency"
                (input)="onFieldChange('defaultCurrency', defaultCurrency.value)"
                placeholder="USD"
                maxlength="3"
              />
            </label>

            <label class="field-block vendor-field-grid-wide">
              <span class="field-label">Description</span>
              <textarea
                #vendorDescription
                class="kt-input min-h-20"
                [value]="form().description"
                (input)="onFieldChange('description', vendorDescription.value)"
                placeholder="Short description"
              ></textarea>
            </label>
          </div>
        </section>

        <section class="vendor-section">
          <h3 class="vendor-section-title">Contact &amp; Address</h3>
          <div class="vendor-field-grid">
            <label class="field-block">
              <span class="field-label">Address Line 1</span>
              <input
                #addressLine1
                class="kt-input"
                [value]="form().addressLine1"
                (input)="onFieldChange('addressLine1', addressLine1.value)"
                placeholder="Street address"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Address Line 2</span>
              <input
                #addressLine2
                class="kt-input"
                [value]="form().addressLine2"
                (input)="onFieldChange('addressLine2', addressLine2.value)"
                placeholder="Suite, floor, etc."
              />
            </label>

            <label class="field-block">
              <span class="field-label">City</span>
              <input
                #city
                class="kt-input"
                [value]="form().city"
                (input)="onFieldChange('city', city.value)"
              />
            </label>

            <label class="field-block">
              <span class="field-label">State / Province</span>
              <input
                #stateProvince
                class="kt-input"
                [value]="form().stateProvince"
                (input)="onFieldChange('stateProvince', stateProvince.value)"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Postal Code</span>
              <input
                #postalCode
                class="kt-input"
                [value]="form().postalCode"
                (input)="onFieldChange('postalCode', postalCode.value)"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Country Code</span>
              <input
                #countryCode
                class="kt-input"
                [value]="form().countryCode"
                (input)="onFieldChange('countryCode', countryCode.value)"
                placeholder="US"
                maxlength="3"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Phone Number</span>
              <input
                #phoneNumber
                class="kt-input"
                [value]="form().phoneNumber"
                (input)="onFieldChange('phoneNumber', phoneNumber.value)"
                placeholder="+1 555 000 0000"
              />
            </label>
          </div>
        </section>

        <section class="vendor-section">
          <h3 class="vendor-section-title">Billing Concepts *</h3>
          <div class="vendor-concept-box">
            @for (type of invoiceTypeOptions(); track type.id) {
              <label class="vendor-concept-item">
                <input
                  type="checkbox"
                  [checked]="hasInvoiceType(type.id)"
                  (change)="toggleInvoiceType(type.id, $any($event.target).checked)"
                />
                <span>{{ type.name }}</span>
              </label>
            }
          </div>
        </section>

        <section class="vendor-section">
          <h3 class="vendor-section-title">Documentation</h3>
          <input
            type="file"
            class="kt-input"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            [disabled]="isSaving()"
            (change)="onAttachmentSelected($any($event.target).files)"
          />
          @if (attachmentName()) {
            <div class="text-xs text-secondary-foreground flex items-center gap-2 mt-2">
              <span>{{ attachmentName() }}</span>
              <button type="button" class="kt-btn kt-btn-xs kt-btn-outline" [disabled]="isSaving()" (click)="clearAttachment()">
                Remove
              </button>
            </div>
          } @else {
            <div class="text-xs text-secondary-foreground mt-2">No documentation attached.</div>
          }
        </section>
      </div>
      <div class="kt-card-footer justify-end gap-2">
        <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSaving()" (click)="closeEditor()">Cancel</button>
        <button type="button" class="kt-btn kt-btn-primary" [disabled]="isSaving()" (click)="saveVendor()">
          @if (isSaving()) {
            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
            {{ attachmentFile() ? 'Saving & uploading...' : 'Saving...' }}
          } @else {
            Save Vendor
          }
        </button>
      </div>
    </section>
  }
</main>
